import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type {
  Standard,
  Element as StandardElement,
  Candidate,
  Group,
  GroupSession,
  Expediente,
  Instrumento,
  Criterio,
} from "@/data";
import {
  buildSeed,
  type Rule,
  type AppUser,
  type AuditLogEntry,
  type SeedState,
  type Evaluation,
  type Dictamen,
  type ExpedienteDoc,
  type ExamAttempt,
  type DiagnosticBankItem,
  type TheoreticalBankQuestion,
  type PrechecklistItem,
  type PrechecklistState,
  type PracticalScore,
  type AttendanceMark,
  type AttendanceEstado,
  type GroupMaterial,
  type GroupMaterialTipo,
  type CandidateObservation,
  type ExamAvailability,
  type LmsRoute,
  type LmsProgressState,
  type LmsAttempt,
  type PreopChecklistRun,
  type PreopChecklistEntry,
  type PreopEstado,
  LMS_QUIZ_PASS_THRESHOLD,
  LMS_PREOP_ITEMS_EC1781,
  buildEc1781Bundle,
} from "@/data/seed";

export type Role = "admin" | "alumno" | "evaluador" | "dictaminador" | "instructor" | "auditor";

export interface ChatMessage {
  id: string;
  ts: string;
  autor: "user" | "ia";
  texto: string;
  meta?: Record<string, unknown>;
}

export const roleLabels: Record<Role, string> = {
  admin: "Administrador académico",
  alumno: "Candidato",
  evaluador: "Evaluador",
  dictaminador: "Dictaminador",
  instructor: "Instructor",
  auditor: "Auditor",
};

export const roleHomes: Record<Role, string> = {
  admin: "/admin",
  alumno: "/alumno",
  evaluador: "/evaluador",
  dictaminador: "/dictaminador",
  instructor: "/instructor",
  auditor: "/auditor",
};

export type AppMode = "demo-guiada" | "libre";

export interface DemoMutations {
  enrolledCandidateIds: string[];
  diagnosticDone: boolean;
  theoreticalScore: number | null;
  practicalCriteriaScored: Record<string, "competente" | "todavia-no" | null>;
  prechecklistDone: boolean;
  dictamenIssued: Record<string, "competente" | "todavia-no" | null>;
  certificatesIssued: string[];
  standardPublished: boolean;
}

const initialMutations: DemoMutations = {
  enrolledCandidateIds: ["cand-001", "cand-002", "cand-003", "cand-004", "cand-005", "cand-006"],
  diagnosticDone: false,
  theoreticalScore: null,
  practicalCriteriaScored: {},
  prechecklistDone: false,
  dictamenIssued: {},
  certificatesIssued: [],
  standardPublished: true,
};

interface SettingsSlice {
  mode: AppMode;
  effectiveUserId: string | null;
}

interface DemoState extends SeedState {
  // Schema
  schemaVersion: number;

  // Settings + identity
  activeRole: Role;
  settings: SettingsSlice;

  // Back-compat mutation flags used by guided pages
  mutations: DemoMutations;

  // Identity / settings
  setRole: (r: Role) => void;
  setMode: (m: AppMode) => void;
  setEffectiveUser: (userId: string | null) => void;

  // Standards CRUD
  addStandard: (s: Standard) => void;
  updateStandard: (codigo: string, patch: Partial<Standard>) => void;
  removeStandard: (codigo: string) => void;
  restoreStandard: (codigo: string) => void;
  publishStandard: (codigo?: string) => void;

  // Elements / criteria / instruments
  setElementsForStandard: (codigo: string, elements: StandardElement[]) => void;
  upsertElement: (codigo: string, element: StandardElement) => void;
  removeElement: (codigo: string, elementId: string) => void;
  upsertCriterio: (codigo: string, elementId: string, criterio: Criterio) => void;
  removeCriterio: (codigo: string, elementId: string, criterioId: string) => void;
  upsertInstrumento: (codigo: string, elementId: string, instrumento: Instrumento) => void;
  removeInstrumento: (codigo: string, elementId: string, instrumentoId: string) => void;

  // Rules CRUD
  setRulesForStandard: (codigo: string, rules: Rule[]) => void;
  upsertRule: (codigo: string, rule: Rule) => void;
  removeRule: (codigo: string, ruleId: string) => void;

  // Groups CRUD
  addGroup: (g: Group) => void;
  updateGroup: (id: string, patch: Partial<Group>) => void;
  removeGroup: (id: string) => void;

  // Sessions CRUD
  setSessionsForGroup: (grupoId: string, sessions: GroupSession[]) => void;
  upsertSession: (grupoId: string, session: GroupSession) => void;
  removeSession: (grupoId: string, sessionId: string) => void;

  // Candidates CRUD
  addCandidate: (c: Candidate) => void;
  updateCandidate: (id: string, patch: Partial<Candidate>) => void;
  removeCandidate: (id: string) => void;

  // Expedientes
  upsertExpediente: (e: Expediente) => void;
  updateExpediente: (id: string, patch: Partial<Expediente>) => void;
  removeExpediente: (id: string) => void;

  // Instruments (first-class, per standard)
  upsertInstrument: (codigo: string, instrumento: Instrumento) => void;
  removeInstrument: (codigo: string, instrumentId: string) => void;
  setInstrumentsForStandard: (codigo: string, instruments: Instrumento[]) => void;

  // Evaluations
  addEvaluation: (e: Evaluation) => void;
  updateEvaluation: (id: string, patch: Partial<Evaluation>) => void;
  removeEvaluation: (id: string) => void;

  // Dictamenes
  addDictamen: (d: Dictamen) => void;
  updateDictamen: (id: string, patch: Partial<Dictamen>) => void;
  removeDictamen: (id: string) => void;

  // Bulk import (used by CRUD hooks)
  bulkImport: <K extends "standards" | "groups" | "candidates" | "expedientes" | "users" | "evaluations" | "dictamenes">(
    slice: K,
    items: Array<DemoState[K] extends Record<string, infer V> ? V & { id?: string; codigo?: string } : never>
  ) => void;

  // Users CRUD
  addUser: (u: AppUser) => void;
  updateUser: (id: string, patch: Partial<AppUser>) => void;
  removeUser: (id: string) => void;

  // Audit log
  logEvent: (entry: Omit<AuditLogEntry, "id" | "ts">) => void;

  // Enrollments (candidato → estándares)
  enrollCandidateInStandard: (candidatoId: string, codigo: string, grupoId?: string) => void;
  unenrollCandidateFromStandard: (candidatoId: string, codigo: string) => void;

  // Expediente documents (per expediente)
  setExpedienteDocs: (expedienteId: string, docs: ExpedienteDoc[]) => void;
  updateExpedienteDoc: (expedienteId: string, docId: string, patch: Partial<ExpedienteDoc>) => void;

  // Exam attempts
  addExamAttempt: (attempt: ExamAttempt) => void;

  // Banks
  setDiagnosticBank: (codigo: string, items: DiagnosticBankItem[]) => void;
  setTheoreticalBank: (codigo: string, items: TheoreticalBankQuestion[]) => void;

  // Prechecklist (per standard template, per expediente state)
  setPrechecklistTemplate: (codigo: string, items: PrechecklistItem[]) => void;
  togglePrechecklistItem: (expedienteId: string, itemId: string, checked: boolean) => void;
  resetPrechecklist: (expedienteId: string) => void;
  markPrechecklistComplete: (expedienteId: string, evaluadorId: string) => void;

  // Practical evaluation (per expediente, per criterio)
  setPracticalCriterion: (expedienteId: string, criterioId: string, patch: Partial<PracticalScore>) => void;
  setPracticalNotes: (expedienteId: string, notes: string) => void;
  submitPracticalEvaluation: (expedienteId: string, evaluadorId: string) => { ok: boolean; missing?: number };

  // Dictamen emission / revocation
  emitirDictamen: (
    expedienteId: string,
    dictaminadorId: string,
    resultado: "competente" | "todavia-no",
    fundamento: string,
    recomendacionIA?: "competente" | "todavia-no",
  ) => { ok: boolean; folio?: string; error?: string };
  revocarCertificado: (
    expedienteId: string,
    dictaminadorId: string,
    motivo: string,
  ) => { ok: boolean; error?: string };

  // Back-compat mutators (used by guided demo screens)
  enrollCandidate: (id: string) => void;
  unenrollCandidate: (id: string) => void;
  completeDiagnostic: () => void;
  setTheoreticalScore: (n: number) => void;
  scoreCriterion: (id: string, v: "competente" | "todavia-no") => void;
  completePrechecklist: () => void;
  issueDictamen: (expedienteId: string, v: "competente" | "todavia-no") => void;
  issueCertificate: (expedienteId: string) => void;

  // Instructor — asistencias
  setAttendance: (grupoId: string, sesionId: string, candidatoId: string, mark: Partial<AttendanceMark> & { estado: AttendanceEstado }) => void;
  clearAttendance: (grupoId: string, sesionId: string, candidatoId: string) => void;

  // Instructor — avance por candidato
  setProgressTopic: (expedienteId: string, temaId: string, completado: boolean) => void;
  resetProgress: (expedienteId: string) => void;

  // Instructor — material por grupo
  addGroupMaterial: (grupoId: string, material: Omit<GroupMaterial, "id" | "creadoEn"> & { id?: string; creadoEn?: string }) => void;
  updateGroupMaterial: (grupoId: string, materialId: string, patch: Partial<GroupMaterial>) => void;
  removeGroupMaterial: (grupoId: string, materialId: string) => void;

  // Instructor — observaciones por candidato
  addCandidateObservation: (obs: Omit<CandidateObservation, "id" | "ts"> & { id?: string; ts?: string }) => void;
  removeCandidateObservation: (candidatoId: string, obsId: string) => void;

  // Instructor — habilitación de exámenes
  setExamAvailability: (expedienteId: string, patch: Partial<ExamAvailability>, actor?: string) => void;

  // LMS — Ruta formativa
  setLmsRoute: (codigo: string, route: LmsRoute) => void;
  submitLmsQuiz: (
    expedienteId: string,
    moduloId: string,
    respuestas: number[],
    actor?: string,
  ) => { ok: boolean; aciertos: number; total: number; aprobado: boolean };
  recordPreopRun: (
    expedienteId: string,
    moduloId: string,
    entries: PreopChecklistEntry[],
    actor?: string,
  ) => { ok: boolean; aprobado: boolean; completas: number; total: number; criticasFaltantes: number };
  resetLmsProgress: (expedienteId: string) => void;

  // Asistente IA simulado por rol
  iaConversationsByRole: Partial<Record<Role, ChatMessage[]>>;
  appendIaMessage: (role: Role, msg: Omit<ChatMessage, "id" | "ts"> & { id?: string; ts?: string }) => void;
  clearIaConversation: (role: Role) => void;

  // EC1781 — popula candidatos, grupos, expedientes, LMS y demás al dar de alta el estándar.
  seedEc1781Bundle: () => void;

  // Utilities
  resetDemo: () => void;
  exportState: () => string;
  importState: (json: string) => { ok: boolean; error?: string };
}

// SCHEMA_VERSION sigue usándose como marca de versión en los snapshots
// JSON que exporta/importa el menú de Utilidades (no es persist automática:
// el store vive sólo en memoria).
const SCHEMA_VERSION = 15;

function freshSeed() {
  return buildSeed();
}

function nextId(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

export const useDemoStore = create<DemoState>()(
  persist(
    (set, get) => ({
      schemaVersion: SCHEMA_VERSION,
      activeRole: "admin",
      settings: { mode: "libre", effectiveUserId: null },
      mutations: initialMutations,
      ...freshSeed(),
      iaConversationsByRole: {},

      setRole: (activeRole) => set({ activeRole }),
      setMode: (mode) => set((s) => ({ settings: { ...s.settings, mode } })),
      setEffectiveUser: (effectiveUserId) =>
        set((s) => ({ settings: { ...s.settings, effectiveUserId } })),

      // Standards
      addStandard: (s) =>
        set((st) => {
          const { [s.codigo]: _removed, ...restArchived } = st.archivedStandards;
          return { standards: { ...st.standards, [s.codigo]: s }, archivedStandards: restArchived };
        }),
      updateStandard: (codigo, patch) =>
        set((st) => {
          const cur = st.standards[codigo];
          if (!cur) return {};
          return { standards: { ...st.standards, [codigo]: { ...cur, ...patch } } };
        }),
      removeStandard: (codigo) =>
        set((st) => {
          const cur = st.standards[codigo];
          if (!cur) return {};
          const { [codigo]: _gone, ...rest } = st.standards;
          return {
            standards: rest,
            archivedStandards: { ...st.archivedStandards, [codigo]: cur },
          };
        }),
      restoreStandard: (codigo) =>
        set((st) => {
          const cur = st.archivedStandards[codigo];
          if (!cur) return {};
          const { [codigo]: _gone, ...rest } = st.archivedStandards;
          return {
            archivedStandards: rest,
            standards: { ...st.standards, [codigo]: cur },
          };
        }),
      publishStandard: (codigo) =>
        set((st) => {
          if (!codigo) {
            return { mutations: { ...st.mutations, standardPublished: true } };
          }
          const cur = st.standards[codigo];
          if (!cur) return {};
          return {
            standards: { ...st.standards, [codigo]: { ...cur, estado: "publicado" } },
            mutations: { ...st.mutations, standardPublished: true },
          };
        }),

      // Elements
      setElementsForStandard: (codigo, elements) =>
        set((st) => ({ elementsByStandard: { ...st.elementsByStandard, [codigo]: elements } })),
      upsertElement: (codigo, element) =>
        set((st) => {
          const list = st.elementsByStandard[codigo] ?? [];
          const idx = list.findIndex((e) => e.id === element.id);
          const next = idx === -1 ? [...list, element] : list.map((e, i) => (i === idx ? element : e));
          return { elementsByStandard: { ...st.elementsByStandard, [codigo]: next } };
        }),
      removeElement: (codigo, elementId) =>
        set((st) => {
          const list = st.elementsByStandard[codigo] ?? [];
          return { elementsByStandard: { ...st.elementsByStandard, [codigo]: list.filter((e) => e.id !== elementId) } };
        }),
      upsertCriterio: (codigo, elementId, criterio) =>
        set((st) => {
          const list = st.elementsByStandard[codigo] ?? [];
          const next = list.map((e) => {
            if (e.id !== elementId) return e;
            const idx = e.criterios.findIndex((c) => c.id === criterio.id);
            const crit = idx === -1 ? [...e.criterios, criterio] : e.criterios.map((c, i) => (i === idx ? criterio : c));
            return { ...e, criterios: crit };
          });
          return { elementsByStandard: { ...st.elementsByStandard, [codigo]: next } };
        }),
      removeCriterio: (codigo, elementId, criterioId) =>
        set((st) => {
          const list = st.elementsByStandard[codigo] ?? [];
          const next = list.map((e) =>
            e.id === elementId ? { ...e, criterios: e.criterios.filter((c) => c.id !== criterioId) } : e
          );
          return { elementsByStandard: { ...st.elementsByStandard, [codigo]: next } };
        }),
      upsertInstrumento: (codigo, elementId, instrumento) =>
        set((st) => {
          const list = st.elementsByStandard[codigo] ?? [];
          const next = list.map((e) => {
            if (e.id !== elementId) return e;
            const idx = e.instrumentos.findIndex((i) => i.id === instrumento.id);
            const inst = idx === -1 ? [...e.instrumentos, instrumento] : e.instrumentos.map((i, k) => (k === idx ? instrumento : i));
            return { ...e, instrumentos: inst };
          });
          return { elementsByStandard: { ...st.elementsByStandard, [codigo]: next } };
        }),
      removeInstrumento: (codigo, elementId, instrumentoId) =>
        set((st) => {
          const list = st.elementsByStandard[codigo] ?? [];
          const next = list.map((e) =>
            e.id === elementId ? { ...e, instrumentos: e.instrumentos.filter((i) => i.id !== instrumentoId) } : e
          );
          return { elementsByStandard: { ...st.elementsByStandard, [codigo]: next } };
        }),

      // Rules
      setRulesForStandard: (codigo, rules) =>
        set((st) => ({ rulesByStandard: { ...st.rulesByStandard, [codigo]: rules } })),
      upsertRule: (codigo, rule) =>
        set((st) => {
          const list = st.rulesByStandard[codigo] ?? [];
          const idx = list.findIndex((r) => r.id === rule.id);
          const next = idx === -1 ? [...list, rule] : list.map((r, i) => (i === idx ? rule : r));
          return { rulesByStandard: { ...st.rulesByStandard, [codigo]: next } };
        }),
      removeRule: (codigo, ruleId) =>
        set((st) => {
          const list = st.rulesByStandard[codigo] ?? [];
          return { rulesByStandard: { ...st.rulesByStandard, [codigo]: list.filter((r) => r.id !== ruleId) } };
        }),

      // Groups
      addGroup: (g) => set((st) => ({ groups: { ...st.groups, [g.id]: g } })),
      updateGroup: (id, patch) =>
        set((st) => {
          const cur = st.groups[id];
          if (!cur) return {};
          return { groups: { ...st.groups, [id]: { ...cur, ...patch } } };
        }),
      removeGroup: (id) =>
        set((st) => {
          const { [id]: _gone, ...rest } = st.groups;
          const { [id]: _gone2, ...sessRest } = st.sessionsByGroup;
          return { groups: rest, sessionsByGroup: sessRest };
        }),

      // Sessions
      setSessionsForGroup: (grupoId, sessions) =>
        set((st) => ({ sessionsByGroup: { ...st.sessionsByGroup, [grupoId]: sessions } })),
      upsertSession: (grupoId, session) =>
        set((st) => {
          const list = st.sessionsByGroup[grupoId] ?? [];
          const idx = list.findIndex((s) => s.id === session.id);
          const next = idx === -1 ? [...list, session] : list.map((s, i) => (i === idx ? session : s));
          return { sessionsByGroup: { ...st.sessionsByGroup, [grupoId]: next } };
        }),
      removeSession: (grupoId, sessionId) =>
        set((st) => {
          const list = st.sessionsByGroup[grupoId] ?? [];
          return { sessionsByGroup: { ...st.sessionsByGroup, [grupoId]: list.filter((s) => s.id !== sessionId) } };
        }),

      // Candidates
      addCandidate: (c) => set((st) => ({ candidates: { ...st.candidates, [c.id]: c } })),
      updateCandidate: (id, patch) =>
        set((st) => {
          const cur = st.candidates[id];
          if (!cur) return {};
          return { candidates: { ...st.candidates, [id]: { ...cur, ...patch } } };
        }),
      removeCandidate: (id) =>
        set((st) => {
          const { [id]: _gone, ...rest } = st.candidates;
          return { candidates: rest };
        }),

      // Expedientes
      upsertExpediente: (e) =>
        set((st) => ({ expedientes: { ...st.expedientes, [e.id]: e } })),
      updateExpediente: (id, patch) =>
        set((st) => {
          const cur = st.expedientes[id];
          if (!cur) return {};
          return { expedientes: { ...st.expedientes, [id]: { ...cur, ...patch } } };
        }),
      removeExpediente: (id) =>
        set((st) => {
          const { [id]: _gone, ...rest } = st.expedientes;
          return { expedientes: rest };
        }),

      // Instruments
      upsertInstrument: (codigo, instrumento) =>
        set((st) => {
          const list = st.instrumentsByStandard[codigo] ?? [];
          const idx = list.findIndex((i) => i.id === instrumento.id);
          const next = idx === -1 ? [...list, instrumento] : list.map((i, k) => (k === idx ? instrumento : i));
          return { instrumentsByStandard: { ...st.instrumentsByStandard, [codigo]: next } };
        }),
      removeInstrument: (codigo, instrumentId) =>
        set((st) => {
          const list = st.instrumentsByStandard[codigo] ?? [];
          return {
            instrumentsByStandard: {
              ...st.instrumentsByStandard,
              [codigo]: list.filter((i) => i.id !== instrumentId),
            },
          };
        }),
      setInstrumentsForStandard: (codigo, instruments) =>
        set((st) => ({
          instrumentsByStandard: { ...st.instrumentsByStandard, [codigo]: instruments },
        })),

      // Evaluations
      addEvaluation: (e) => set((st) => ({ evaluations: { ...st.evaluations, [e.id]: e } })),
      updateEvaluation: (id, patch) =>
        set((st) => {
          const cur = st.evaluations[id];
          if (!cur) return {};
          return { evaluations: { ...st.evaluations, [id]: { ...cur, ...patch } } };
        }),
      removeEvaluation: (id) =>
        set((st) => {
          const { [id]: _gone, ...rest } = st.evaluations;
          return { evaluations: rest };
        }),

      // Dictamenes
      addDictamen: (d) => set((st) => ({ dictamenes: { ...st.dictamenes, [d.id]: d } })),
      updateDictamen: (id, patch) =>
        set((st) => {
          const cur = st.dictamenes[id];
          if (!cur) return {};
          return { dictamenes: { ...st.dictamenes, [id]: { ...cur, ...patch } } };
        }),
      removeDictamen: (id) =>
        set((st) => {
          const { [id]: _gone, ...rest } = st.dictamenes;
          return { dictamenes: rest };
        }),

      // Bulk import (idempotent merge keyed by id/codigo)
      bulkImport: (slice, items) =>
        set((st) => {
          const target = (st as any)[slice] as Record<string, any>;
          const next: Record<string, any> = { ...target };
          for (const it of items as any[]) {
            const key = (it.id ?? it.codigo) as string | undefined;
            if (!key) continue;
            next[key] = { ...next[key], ...it };
          }
          return { [slice]: next } as any;
        }),

      // Users
      addUser: (u) => set((st) => ({ users: { ...st.users, [u.id]: u } })),
      updateUser: (id, patch) =>
        set((st) => {
          const cur = st.users[id];
          if (!cur) return {};
          return { users: { ...st.users, [id]: { ...cur, ...patch } } };
        }),
      removeUser: (id) =>
        set((st) => {
          const { [id]: _gone, ...rest } = st.users;
          return { users: rest };
        }),

      // Audit log
      logEvent: (entry) =>
        set((st) => ({
          auditLog: [
            { id: nextId("evt"), ts: new Date().toISOString(), ...entry },
            ...st.auditLog,
          ].slice(0, 500),
        })),

      // Enrollments
      enrollCandidateInStandard: (candidatoId, codigo, grupoId) =>
        set((st) => {
          const cur = st.enrollments[candidatoId] ?? [];
          const alreadyEnrolled = cur.includes(codigo);
          const nextEnr = alreadyEnrolled
            ? st.enrollments
            : { ...st.enrollments, [candidatoId]: [...cur, codigo] };
          // Auto-create an empty expediente if none exists for this pair
          const existing = Object.values(st.expedientes).find(
            (e) => e.candidatoId === candidatoId && e.estandar === codigo
          );
          if (existing) return { enrollments: nextEnr };
          const newId = nextId("exp");
          const newExp = {
            id: newId,
            candidatoId,
            estandar: codigo,
            grupoId: grupoId ?? "",
            diagnostico: { aplicado: false, puntaje: null, fecha: "—" },
            examenTeorico: { aplicado: false, puntaje: null, aprobado: false, fecha: "—" },
            evaluacionPractica: { aplicada: false, criteriosCompetente: 0, criteriosTotales: 0, fecha: "—" },
            evidencias: { entregadas: 0, requeridas: 0, verificadas: 0 },
            dictamen: "pendiente" as const,
            certificado: { emitido: false, folio: null, fechaEmision: null },
            alertasIA: [],
          };
          const els = st.elementsByStandard[codigo] ?? [];
          const allEv = els.flatMap((el) => el.evidencias);
          const docs: ExpedienteDoc[] = allEv.map((ev) => ({
            id: `${newId}-${ev.id}`,
            nombre: ev.nombre,
            tipo: ev.tipo,
            obligatoria: ev.obligatoria,
            criterioIds: ev.criterioIds,
            estado: "faltante",
          }));
          const actor = st.settings.effectiveUserId ?? "admin-001";
          const actorRol = (st.users[actor]?.rol ?? "admin") as Role;
          return {
            enrollments: nextEnr,
            expedientes: { ...st.expedientes, [newId]: newExp },
            expedienteDocs: { ...st.expedienteDocs, [newId]: docs },
            auditLog: [
              { id: nextId("evt"), ts: new Date().toISOString(), actor, rol: actorRol, accion: `inscribió candidato en ${codigo}${grupoId ? ` (grupo ${grupoId})` : ""}`, entidad: "expediente", entidadId: newId } as AuditLogEntry,
              ...st.auditLog,
            ].slice(0, 500),
          };
        }),
      unenrollCandidateFromStandard: (candidatoId, codigo) =>
        set((st) => {
          const cur = st.enrollments[candidatoId] ?? [];
          if (!cur.includes(codigo)) return {};
          return { enrollments: { ...st.enrollments, [candidatoId]: cur.filter((c) => c !== codigo) } };
        }),

      // Expediente docs
      setExpedienteDocs: (expedienteId, docs) =>
        set((st) => ({ expedienteDocs: { ...st.expedienteDocs, [expedienteId]: docs } })),
      updateExpedienteDoc: (expedienteId, docId, patch) =>
        set((st) => {
          const list = st.expedienteDocs[expedienteId] ?? [];
          const next = list.map((d) => (d.id === docId ? { ...d, ...patch } : d));
          return { expedienteDocs: { ...st.expedienteDocs, [expedienteId]: next } };
        }),

      // Exam attempts
      addExamAttempt: (attempt) =>
        set((st) => {
          const list = st.examAttempts[attempt.expedienteId] ?? [];
          return {
            examAttempts: { ...st.examAttempts, [attempt.expedienteId]: [...list, attempt] },
          };
        }),

      // Banks
      setDiagnosticBank: (codigo, items) =>
        set((st) => ({ diagnosticBankByStandard: { ...st.diagnosticBankByStandard, [codigo]: items } })),
      setTheoreticalBank: (codigo, items) =>
        set((st) => ({ theoreticalBankByStandard: { ...st.theoreticalBankByStandard, [codigo]: items } })),

      // Prechecklist
      setPrechecklistTemplate: (codigo, items) =>
        set((st) => ({ prechecklistTemplateByStandard: { ...st.prechecklistTemplateByStandard, [codigo]: items } })),
      togglePrechecklistItem: (expedienteId, itemId, checked) =>
        set((st) => {
          const exp = st.expedientes[expedienteId];
          const tpl = (exp && st.prechecklistTemplateByStandard[exp.estandar]) ?? [];
          const cur = st.prechecklistByExpediente[expedienteId] ?? {
            entries: tpl.map((i) => ({ itemId: i.id, checked: false })),
          };
          const has = cur.entries.some((e) => e.itemId === itemId);
          const entries = has
            ? cur.entries.map((e) => (e.itemId === itemId ? { ...e, checked } : e))
            : [...cur.entries, { itemId, checked }];
          return {
            prechecklistByExpediente: {
              ...st.prechecklistByExpediente,
              [expedienteId]: { ...cur, entries, completedAt: undefined },
            },
          };
        }),
      resetPrechecklist: (expedienteId) =>
        set((st) => {
          const exp = st.expedientes[expedienteId];
          const tpl = (exp && st.prechecklistTemplateByStandard[exp.estandar]) ?? [];
          return {
            prechecklistByExpediente: {
              ...st.prechecklistByExpediente,
              [expedienteId]: { entries: tpl.map((i) => ({ itemId: i.id, checked: false })) },
            },
          };
        }),
      markPrechecklistComplete: (expedienteId, evaluadorId) =>
        set((st) => {
          const exp = st.expedientes[expedienteId];
          const tpl = (exp && st.prechecklistTemplateByStandard[exp.estandar]) ?? [];
          const cur = st.prechecklistByExpediente[expedienteId] ?? {
            entries: tpl.map((i) => ({ itemId: i.id, checked: false })),
          };
          const now = new Date();
          return {
            prechecklistByExpediente: {
              ...st.prechecklistByExpediente,
              [expedienteId]: { ...cur, completedAt: now.toISOString().slice(0, 10), evaluadorId },
            },
            auditLog: [
              { id: nextId("evt"), ts: now.toISOString(), actor: evaluadorId, rol: "evaluador" as Role, accion: "firmó lista preoperativa", entidad: "expediente", entidadId: expedienteId } as AuditLogEntry,
              ...st.auditLog,
            ].slice(0, 500),
          };
        }),

      // Practical evaluation
      setPracticalCriterion: (expedienteId, criterioId, patch) =>
        set((st) => {
          const cur = st.practicalScoresByExpediente[expedienteId] ?? {};
          const prev = cur[criterioId] ?? { resultado: "competente" as const };
          return {
            practicalScoresByExpediente: {
              ...st.practicalScoresByExpediente,
              [expedienteId]: {
                ...cur,
                [criterioId]: { ...prev, ...patch, fecha: new Date().toISOString().slice(0, 10) },
              },
            },
          };
        }),
      setPracticalNotes: (expedienteId, notes) =>
        set((st) => ({
          practicalNotesByExpediente: { ...st.practicalNotesByExpediente, [expedienteId]: notes },
        })),
      submitPracticalEvaluation: (expedienteId, evaluadorId) => {
        const st = get();
        const exp = st.expedientes[expedienteId];
        if (!exp) return { ok: false, missing: 0 };
        const els = st.elementsByStandard[exp.estandar] ?? [];
        const allCrits = els.flatMap((el) => el.criterios);
        const scores = st.practicalScoresByExpediente[expedienteId] ?? {};
        const missing = allCrits.filter((c) => !scores[c.id]).length;
        if (missing > 0) return { ok: false, missing };
        const competente = allCrits.filter((c) => scores[c.id]?.resultado === "competente").length;
        const today = new Date().toISOString().slice(0, 10);
        set((s) => ({
          expedientes: {
            ...s.expedientes,
            [expedienteId]: {
              ...exp,
              evaluacionPractica: {
                aplicada: true,
                criteriosCompetente: competente,
                criteriosTotales: allCrits.length,
                fecha: today,
              },
              dictamen: exp.dictamen === "pendiente" ? "pendiente" : exp.dictamen,
            },
          },
          practicalScoresByExpediente: {
            ...s.practicalScoresByExpediente,
            [expedienteId]: Object.fromEntries(
              Object.entries(scores).map(([k, v]) => [k, { ...v, evaluadorId: v.evaluadorId ?? evaluadorId }]),
            ),
          },
          auditLog: [
            { id: nextId("evt"), ts: new Date().toISOString(), actor: evaluadorId, rol: "evaluador" as Role, accion: "envió evaluación práctica", entidad: "expediente", entidadId: expedienteId } as AuditLogEntry,
            ...s.auditLog,
          ].slice(0, 500),
        }));
        return { ok: true };
      },

      // Back-compat mutators
      enrollCandidate: (id) =>
        set((s) => ({
          mutations: {
            ...s.mutations,
            enrolledCandidateIds: Array.from(new Set([...s.mutations.enrolledCandidateIds, id])),
          },
        })),
      unenrollCandidate: (id) =>
        set((s) => ({
          mutations: {
            ...s.mutations,
            enrolledCandidateIds: s.mutations.enrolledCandidateIds.filter((x) => x !== id),
          },
        })),
      completeDiagnostic: () =>
        set((s) => ({ mutations: { ...s.mutations, diagnosticDone: true } })),
      setTheoreticalScore: (n) =>
        set((s) => ({ mutations: { ...s.mutations, theoreticalScore: n } })),
      scoreCriterion: (id, v) =>
        set((s) => ({
          mutations: {
            ...s.mutations,
            practicalCriteriaScored: { ...s.mutations.practicalCriteriaScored, [id]: v },
          },
        })),
      completePrechecklist: () =>
        set((s) => ({ mutations: { ...s.mutations, prechecklistDone: true } })),
      emitirDictamen: (expedienteId, dictaminadorId, resultado, fundamento, recomendacionIA) => {
        const s = get();
        const exp = s.expedientes[expedienteId];
        if (!exp) return { ok: false, error: "Expediente no encontrado" };
        if (!fundamento || fundamento.trim().length < 10) {
          return { ok: false, error: "Justificación obligatoria (mínimo 10 caracteres)" };
        }
        const now = new Date();
        const isoDate = now.toISOString().slice(0, 10);
        const dictamenId = `dict-${expedienteId}`;
        const expedienteDictamen: Expediente["dictamen"] =
          resultado === "competente" ? "competente" : "todavia-no-competente";

        // Generate folio if competente and not yet emitted (preserve folio on re-emission).
        let nextExp: Expediente = { ...exp, dictamen: expedienteDictamen };
        let folio: string | undefined;
        if (resultado === "competente") {
          if (exp.certificado.emitido && exp.certificado.folio) {
            folio = exp.certificado.folio;
          } else {
            const year = now.getFullYear();
            const issuedCount = Object.values(s.expedientes).filter(
              (e) => e.estandar === exp.estandar && e.certificado.emitido,
            ).length;
            const seq = String(issuedCount + 1).padStart(4, "0");
            folio = `CC-${exp.estandar}-${year}-${seq}`;
          }
          nextExp = {
            ...nextExp,
            certificado: { emitido: true, folio, fechaEmision: isoDate },
          };
        } else {
          // Not competent: ensure no certificate is active.
          nextExp = {
            ...nextExp,
            certificado: { emitido: false, folio: exp.certificado.folio, fechaEmision: exp.certificado.fechaEmision },
          };
        }

        const dictamen: Dictamen = {
          id: dictamenId,
          expedienteId,
          candidatoId: exp.candidatoId,
          estandar: exp.estandar,
          dictaminadorId,
          resultado,
          fundamento: fundamento.trim(),
          fecha: now.toISOString(),
          certificadoId: folio,
          recomendacionIA,
          // re-emission clears prior revocation
          revocacion: undefined,
        };

        set((st) => ({
          expedientes: { ...st.expedientes, [expedienteId]: nextExp },
          dictamenes: { ...st.dictamenes, [dictamenId]: dictamen },
          mutations: {
            ...st.mutations,
            dictamenIssued: { ...st.mutations.dictamenIssued, [expedienteId]: resultado },
            certificatesIssued:
              resultado === "competente"
                ? Array.from(new Set([...st.mutations.certificatesIssued, expedienteId]))
                : st.mutations.certificatesIssued.filter((x) => x !== expedienteId),
          },
          auditLog: [
            {
              id: nextId("evt"),
              ts: now.toISOString(),
              actor: dictaminadorId,
              rol: "dictaminador" as Role,
              accion:
                resultado === "competente"
                  ? `emitió dictamen COMPETENTE${folio ? ` (folio ${folio})` : ""}`
                  : "emitió dictamen TODAVÍA NO COMPETENTE",
              entidad: "expediente",
              entidadId: expedienteId,
            } as AuditLogEntry,
            ...st.auditLog,
          ].slice(0, 500),
        }));
        return { ok: true, folio };
      },
      revocarCertificado: (expedienteId, dictaminadorId, motivo) => {
        const s = get();
        const exp = s.expedientes[expedienteId];
        if (!exp) return { ok: false, error: "Expediente no encontrado" };
        if (!exp.certificado.emitido) return { ok: false, error: "Este expediente no tiene certificado activo" };
        if (!motivo || motivo.trim().length < 5) {
          return { ok: false, error: "Motivo de revocación obligatorio" };
        }
        const now = new Date();
        const dictamenId = `dict-${expedienteId}`;
        const prev = s.dictamenes[dictamenId];
        set((st) => ({
          expedientes: {
            ...st.expedientes,
            [expedienteId]: {
              ...exp,
              certificado: { ...exp.certificado, emitido: false },
            },
          },
          dictamenes: prev
            ? {
                ...st.dictamenes,
                [dictamenId]: {
                  ...prev,
                  revocacion: { motivo: motivo.trim(), fecha: now.toISOString(), actor: dictaminadorId },
                },
              }
            : st.dictamenes,
          mutations: {
            ...st.mutations,
            certificatesIssued: st.mutations.certificatesIssued.filter((x) => x !== expedienteId),
          },
          auditLog: [
            {
              id: nextId("evt"),
              ts: now.toISOString(),
              actor: dictaminadorId,
              rol: "dictaminador" as Role,
              accion: `revocó certificado (motivo: ${motivo.trim()})`,
              entidad: "expediente",
              entidadId: expedienteId,
            } as AuditLogEntry,
            ...st.auditLog,
          ].slice(0, 500),
        }));
        return { ok: true };
      },
      issueDictamen: (expedienteId, v) =>
        set((s) => ({
          mutations: {
            ...s.mutations,
            dictamenIssued: { ...s.mutations.dictamenIssued, [expedienteId]: v },
          },
        })),
      issueCertificate: (expedienteId) =>
        set((s) => ({
          mutations: {
            ...s.mutations,
            certificatesIssued: Array.from(new Set([...s.mutations.certificatesIssued, expedienteId])),
          },
        })),

      // Asistencias
      setAttendance: (grupoId, sesionId, candidatoId, mark) =>
        set((st) => {
          const byGroup = st.attendanceByGroup[grupoId] ?? {};
          const bySession = byGroup[sesionId] ?? {};
          const next: AttendanceMark = {
            estado: mark.estado,
            nota: mark.nota ?? bySession[candidatoId]?.nota,
            registradoEn: new Date().toISOString(),
          };
          return {
            attendanceByGroup: {
              ...st.attendanceByGroup,
              [grupoId]: {
                ...byGroup,
                [sesionId]: { ...bySession, [candidatoId]: next },
              },
            },
          };
        }),
      clearAttendance: (grupoId, sesionId, candidatoId) =>
        set((st) => {
          const byGroup = st.attendanceByGroup[grupoId];
          const bySession = byGroup?.[sesionId];
          if (!bySession || !(candidatoId in bySession)) return {};
          const { [candidatoId]: _gone, ...rest } = bySession;
          return {
            attendanceByGroup: {
              ...st.attendanceByGroup,
              [grupoId]: { ...byGroup, [sesionId]: rest },
            },
          };
        }),

      // Avance
      setProgressTopic: (expedienteId, temaId, completado) =>
        set((st) => {
          const cur = st.progressByExpediente[expedienteId] ?? {};
          return {
            progressByExpediente: {
              ...st.progressByExpediente,
              [expedienteId]: { ...cur, [temaId]: completado },
            },
          };
        }),
      resetProgress: (expedienteId) =>
        set((st) => {
          if (!st.progressByExpediente[expedienteId]) return {};
          const { [expedienteId]: _gone, ...rest } = st.progressByExpediente;
          return { progressByExpediente: rest };
        }),

      // Material por grupo
      addGroupMaterial: (grupoId, material) =>
        set((st) => {
          const list = st.materialsByGroup[grupoId] ?? [];
          const item: GroupMaterial = {
            id: material.id ?? nextId("mat"),
            titulo: material.titulo,
            url: material.url,
            tipo: material.tipo,
            creadoEn: material.creadoEn ?? new Date().toISOString(),
            autorId: material.autorId,
          };
          return {
            materialsByGroup: { ...st.materialsByGroup, [grupoId]: [item, ...list] },
          };
        }),
      updateGroupMaterial: (grupoId, materialId, patch) =>
        set((st) => {
          const list = st.materialsByGroup[grupoId] ?? [];
          const next = list.map((m) => (m.id === materialId ? { ...m, ...patch } : m));
          return { materialsByGroup: { ...st.materialsByGroup, [grupoId]: next } };
        }),
      removeGroupMaterial: (grupoId, materialId) =>
        set((st) => {
          const list = st.materialsByGroup[grupoId] ?? [];
          return {
            materialsByGroup: { ...st.materialsByGroup, [grupoId]: list.filter((m) => m.id !== materialId) },
          };
        }),

      // Observaciones por candidato
      addCandidateObservation: (obs) =>
        set((st) => {
          const list = st.observationsByCandidate[obs.candidatoId] ?? [];
          const item: CandidateObservation = {
            id: obs.id ?? nextId("obs"),
            candidatoId: obs.candidatoId,
            grupoId: obs.grupoId,
            autorId: obs.autorId,
            ts: obs.ts ?? new Date().toISOString(),
            texto: obs.texto,
          };
          return {
            observationsByCandidate: {
              ...st.observationsByCandidate,
              [obs.candidatoId]: [item, ...list],
            },
          };
        }),
      removeCandidateObservation: (candidatoId, obsId) =>
        set((st) => {
          const list = st.observationsByCandidate[candidatoId] ?? [];
          return {
            observationsByCandidate: {
              ...st.observationsByCandidate,
              [candidatoId]: list.filter((o) => o.id !== obsId),
            },
          };
        }),

      // Habilitación de exámenes
      setExamAvailability: (expedienteId, patch, actor) =>
        set((st) => {
          const cur = st.examAvailabilityByExpediente[expedienteId] ?? { diagnostico: true, teorico: true };
          const next: ExamAvailability = {
            ...cur,
            ...patch,
            habilitadoEn: new Date().toISOString(),
            habilitadoPor: actor ?? cur.habilitadoPor,
          };
          const exp = st.expedientes[expedienteId];
          const actorId = actor ?? st.settings.effectiveUserId ?? "instructor";
          const actorRol = (st.users[actorId]?.rol ?? "instructor") as Role;
          return {
            examAvailabilityByExpediente: { ...st.examAvailabilityByExpediente, [expedienteId]: next },
            auditLog: [
              {
                id: nextId("evt"),
                ts: new Date().toISOString(),
                actor: actorId,
                rol: actorRol,
                accion: `actualizó disponibilidad de exámenes (diagnóstico=${next.diagnostico ? "sí" : "no"}, teórico=${next.teorico ? "sí" : "no"})`,
                entidad: "expediente",
                entidadId: exp?.id ?? expedienteId,
              } as AuditLogEntry,
              ...st.auditLog,
            ].slice(0, 500),
          };
        }),

      // LMS — Ruta formativa
      setLmsRoute: (codigo, route) =>
        set((st) => ({ lmsRouteByStandard: { ...st.lmsRouteByStandard, [codigo]: route } })),

      submitLmsQuiz: (expedienteId, moduloId, respuestas, actor) => {
        const st = get();
        const exp = st.expedientes[expedienteId];
        const route = exp ? st.lmsRouteByStandard[exp.estandar] : undefined;
        const mod = route?.modules.find((m) => m.id === moduloId);
        if (!exp || !route || !mod) {
          return { ok: false, aciertos: 0, total: 0, aprobado: false };
        }
        const total = mod.quiz.length;
        let aciertos = 0;
        mod.quiz.forEach((q, i) => {
          if (respuestas[i] === q.correcta) aciertos++;
        });
        const aprobado = aciertos >= Math.min(LMS_QUIZ_PASS_THRESHOLD, total);
        const actorId = actor ?? exp.candidatoId;
        const actorRol = (st.users[actorId]?.rol ?? "alumno") as Role;
        const attempt: LmsAttempt = {
          id: nextId("lms-att"),
          moduloId,
          ts: new Date().toISOString(),
          aciertos,
          total,
          aprobado,
          respuestas: respuestas.slice(0, total),
        };
        set((cur) => {
          const prev = cur.lmsProgressByExpediente[expedienteId] ?? {
            attemptsByModule: {},
            completedModuleIds: [],
          };
          const prevAttempts = prev.attemptsByModule[moduloId] ?? [];
          const completedSet = new Set(prev.completedModuleIds);
          if (aprobado) completedSet.add(moduloId);
          const completedModuleIds = route.modules.filter((m) => completedSet.has(m.id)).map((m) => m.id);
          const rutaCompletadaEn =
            completedModuleIds.length === route.modules.length
              ? prev.rutaCompletadaEn ?? new Date().toISOString()
              : prev.rutaCompletadaEn;
          const nextState: LmsProgressState = {
            ...prev,
            attemptsByModule: {
              ...prev.attemptsByModule,
              [moduloId]: [...prevAttempts, attempt].slice(-5),
            },
            completedModuleIds,
            rutaCompletadaEn,
          };
          return {
            lmsProgressByExpediente: {
              ...cur.lmsProgressByExpediente,
              [expedienteId]: nextState,
            },
            auditLog: [
              {
                id: nextId("evt"),
                ts: new Date().toISOString(),
                actor: actorId,
                rol: actorRol,
                accion: `${aprobado ? "aprobó" : "intentó"} módulo LMS "${mod.titulo}" (${aciertos}/${total})`,
                entidad: "expediente",
                entidadId: expedienteId,
              } as AuditLogEntry,
              ...cur.auditLog,
            ].slice(0, 500),
          };
        });
        return { ok: true, aciertos, total, aprobado };
      },

      recordPreopRun: (expedienteId, moduloId, entries, actor) => {
        const st = get();
        const exp = st.expedientes[expedienteId];
        if (!exp) {
          return { ok: false, aprobado: false, completas: 0, total: 0, criticasFaltantes: 0 };
        }
        const items = LMS_PREOP_ITEMS_EC1781;
        const byId = new Map(entries.map((e) => [e.itemId, e] as const));
        const total = items.length;
        const completas = items.filter((i) => {
          const e = byId.get(i.id);
          return e?.estado === "cumple" || e?.estado === "no-aplica";
        }).length;
        const criticasFaltantes = items.filter((i) => {
          if (!i.critical) return false;
          const e = byId.get(i.id);
          return !e || e.estado === "no-cumple";
        }).length;
        const pct = total > 0 ? (completas / total) * 100 : 0;
        const aprobado = criticasFaltantes === 0 && pct >= 80;
        const actorId = actor ?? exp.candidatoId;
        const actorRol = (st.users[actorId]?.rol ?? "alumno") as Role;
        const run: PreopChecklistRun = {
          id: nextId("preop-run"),
          expedienteId,
          moduloId,
          fecha: new Date().toISOString(),
          entries: entries.map((e) => ({
            itemId: e.itemId,
            estado: e.estado,
            ...(e.observacion && e.observacion.trim() ? { observacion: e.observacion.trim() } : {}),
          })),
          completas,
          total,
          criticasFaltantes,
          aprobado,
        };
        set((cur) => {
          const prev = cur.preopRunsByExpediente[expedienteId] ?? [];
          const lmsPrev = cur.lmsProgressByExpediente[expedienteId] ?? {
            attemptsByModule: {},
            completedModuleIds: [],
          };
          const route = cur.lmsRouteByStandard[exp.estandar];
          const completedSet = new Set(lmsPrev.completedModuleIds);
          if (aprobado) completedSet.add(moduloId);
          const completedModuleIds = route
            ? route.modules.filter((m) => completedSet.has(m.id)).map((m) => m.id)
            : Array.from(completedSet);
          const rutaCompletadaEn =
            route && completedModuleIds.length === route.modules.length
              ? lmsPrev.rutaCompletadaEn ?? new Date().toISOString()
              : lmsPrev.rutaCompletadaEn;
          return {
            preopRunsByExpediente: {
              ...cur.preopRunsByExpediente,
              [expedienteId]: [...prev, run].slice(-10),
            },
            lmsProgressByExpediente: {
              ...cur.lmsProgressByExpediente,
              [expedienteId]: { ...lmsPrev, completedModuleIds, rutaCompletadaEn },
            },
            auditLog: [
              {
                id: nextId("evt"),
                ts: new Date().toISOString(),
                actor: actorId,
                rol: actorRol,
                accion: `${aprobado ? "completó" : "realizó intento de"} simulador preoperativo (${completas}/${total}, críticas faltantes: ${criticasFaltantes})`,
                entidad: "expediente",
                entidadId: expedienteId,
              } as AuditLogEntry,
              ...cur.auditLog,
            ].slice(0, 500),
          };
        });
        return { ok: true, aprobado, completas, total, criticasFaltantes };
      },

      resetLmsProgress: (expedienteId) =>
        set((cur) => ({
          lmsProgressByExpediente: {
            ...cur.lmsProgressByExpediente,
            [expedienteId]: { attemptsByModule: {}, completedModuleIds: [] },
          },
          preopRunsByExpediente: {
            ...cur.preopRunsByExpediente,
            [expedienteId]: [],
          },
        })),

      // Asistente IA simulado por rol
      appendIaMessage: (role, msg) =>
        set((st) => {
          const prev = st.iaConversationsByRole[role] ?? [];
          const entry: ChatMessage = {
            id: msg.id ?? nextId("ia"),
            ts: msg.ts ?? new Date().toISOString(),
            autor: msg.autor,
            texto: msg.texto,
            ...(msg.meta ? { meta: msg.meta } : {}),
          };
          return {
            iaConversationsByRole: { ...st.iaConversationsByRole, [role]: [...prev, entry] },
          };
        }),
      clearIaConversation: (role) =>
        set((st) => {
          const { [role]: _gone, ...rest } = st.iaConversationsByRole;
          return { iaConversationsByRole: rest };
        }),

      seedEc1781Bundle: () =>
        set((st) => {
          const realEls = st.elementsByStandard["EC1781"] ?? [];
          const b = buildEc1781Bundle(realEls);
          const currentStd = st.standards["EC1781"];
          return {
            standards: {
              ...st.standards,
              ...(currentStd
                ? {
                    EC1781: {
                      ...currentStd,
                      candidatosActivos: b.standardPatch.candidatosActivos,
                      certificadosEmitidos: b.standardPatch.certificadosEmitidos,
                    },
                  }
                : {}),
            },
            groups: { ...st.groups, ...b.groups },
            sessionsByGroup: { ...st.sessionsByGroup, ...b.sessionsByGroup },
            candidates: { ...st.candidates, ...b.candidates },
            expedientes: { ...st.expedientes, ...b.expedientes },
            users: { ...st.users, ...b.users },
            enrollments: { ...st.enrollments, ...b.enrollments },
            expedienteDocs: { ...st.expedienteDocs, ...b.expedienteDocs },
            examAttempts: { ...st.examAttempts, ...b.examAttempts },
            diagnosticBankByStandard: { ...st.diagnosticBankByStandard, EC1781: b.diagnosticBank },
            theoreticalBankByStandard: { ...st.theoreticalBankByStandard, EC1781: b.theoreticalBank },
            prechecklistTemplateByStandard: { ...st.prechecklistTemplateByStandard, EC1781: b.prechecklistTemplate },
            prechecklistByExpediente: { ...st.prechecklistByExpediente, ...b.prechecklistByExpediente },
            practicalScoresByExpediente: { ...st.practicalScoresByExpediente, ...b.practicalScoresByExpediente },
            lmsRouteByStandard: { ...st.lmsRouteByStandard, EC1781: b.lmsRoute },
            lmsProgressByExpediente: { ...st.lmsProgressByExpediente, ...b.lmsProgressByExpediente },
          };
        }),

      // Utilities
      resetDemo: () => {
        // El estado vive sólo en memoria (sin persist), así que basta con
        // recargar la página: el store vuelve al seed inicial sin EC1781 ni
        // ningún otro cambio hecho durante la sesión. Por compat, también
        // limpiamos cualquier clave vieja que hubiera quedado en localStorage
        // de versiones previas que sí persistían.
        if (typeof window !== "undefined") {
          try {
            window.localStorage.removeItem("certicore-demo-state");
          } catch {
            // Ignorar: el reload es lo que asegura el estado limpio.
          }
          window.location.reload();
        }
      },
      exportState: () => {
        const s = get();
        const payload = {
          schemaVersion: s.schemaVersion,
          activeRole: s.activeRole,
          settings: s.settings,
          mutations: s.mutations,
          standards: s.standards,
          archivedStandards: s.archivedStandards,
          elementsByStandard: s.elementsByStandard,
          instrumentsByStandard: s.instrumentsByStandard,
          rulesByStandard: s.rulesByStandard,
          groups: s.groups,
          sessionsByGroup: s.sessionsByGroup,
          candidates: s.candidates,
          expedientes: s.expedientes,
          evaluations: s.evaluations,
          dictamenes: s.dictamenes,
          users: s.users,
          auditLog: s.auditLog,
          enrollments: s.enrollments,
          expedienteDocs: s.expedienteDocs,
          examAttempts: s.examAttempts,
          diagnosticBankByStandard: s.diagnosticBankByStandard,
          theoreticalBankByStandard: s.theoreticalBankByStandard,
          prechecklistTemplateByStandard: s.prechecklistTemplateByStandard,
          prechecklistByExpediente: s.prechecklistByExpediente,
          practicalScoresByExpediente: s.practicalScoresByExpediente,
          practicalNotesByExpediente: s.practicalNotesByExpediente,
          attendanceByGroup: s.attendanceByGroup,
          progressByExpediente: s.progressByExpediente,
          materialsByGroup: s.materialsByGroup,
          observationsByCandidate: s.observationsByCandidate,
          examAvailabilityByExpediente: s.examAvailabilityByExpediente,
          lmsRouteByStandard: s.lmsRouteByStandard,
          lmsProgressByExpediente: s.lmsProgressByExpediente,
          preopRunsByExpediente: s.preopRunsByExpediente,
          iaConversationsByRole: s.iaConversationsByRole,
        };
        return JSON.stringify(payload, null, 2);
      },
      importState: (json) => {
        try {
          const parsed = JSON.parse(json);
          if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
            return { ok: false, error: "JSON inválido (se esperaba un objeto)." };
          }
          const p = parsed as Record<string, unknown>;
          const isObj = (v: unknown): v is Record<string, unknown> =>
            !!v && typeof v === "object" && !Array.isArray(v);
          const validRoles: Role[] = ["admin", "alumno", "evaluador", "dictaminador", "instructor", "auditor"];
          const seed = freshSeed();
          const cur = get();
          const activeRole = validRoles.includes(p.activeRole as Role)
            ? (p.activeRole as Role)
            : cur.activeRole;
          const settings: SettingsSlice = isObj(p.settings) && (p.settings.mode === "demo-guiada" || p.settings.mode === "libre")
            ? {
                mode: p.settings.mode as AppMode,
                effectiveUserId: typeof p.settings.effectiveUserId === "string" ? p.settings.effectiveUserId : null,
              }
            : cur.settings;
          const mutations: DemoMutations = isObj(p.mutations)
            ? {
                enrolledCandidateIds: Array.isArray(p.mutations.enrolledCandidateIds)
                  ? (p.mutations.enrolledCandidateIds as string[]).filter((x) => typeof x === "string")
                  : initialMutations.enrolledCandidateIds,
                diagnosticDone: !!p.mutations.diagnosticDone,
                theoreticalScore: typeof p.mutations.theoreticalScore === "number" ? p.mutations.theoreticalScore : null,
                practicalCriteriaScored: isObj(p.mutations.practicalCriteriaScored)
                  ? (p.mutations.practicalCriteriaScored as DemoMutations["practicalCriteriaScored"])
                  : {},
                prechecklistDone: !!p.mutations.prechecklistDone,
                dictamenIssued: isObj(p.mutations.dictamenIssued)
                  ? (p.mutations.dictamenIssued as DemoMutations["dictamenIssued"])
                  : {},
                certificatesIssued: Array.isArray(p.mutations.certificatesIssued)
                  ? (p.mutations.certificatesIssued as string[]).filter((x) => typeof x === "string")
                  : [],
                standardPublished: !!p.mutations.standardPublished,
              }
            : initialMutations;
          const safe: DemoState = {
            ...cur,
            schemaVersion: SCHEMA_VERSION,
            activeRole,
            settings,
            mutations,
            standards: isObj(p.standards) ? (p.standards as Record<string, Standard>) : seed.standards,
            archivedStandards: isObj(p.archivedStandards) ? (p.archivedStandards as Record<string, Standard>) : {},
            elementsByStandard: isObj(p.elementsByStandard)
              ? (p.elementsByStandard as Record<string, StandardElement[]>)
              : seed.elementsByStandard,
            instrumentsByStandard: isObj(p.instrumentsByStandard)
              ? (p.instrumentsByStandard as Record<string, Instrumento[]>)
              : seed.instrumentsByStandard,
            rulesByStandard: isObj(p.rulesByStandard)
              ? (p.rulesByStandard as Record<string, Rule[]>)
              : seed.rulesByStandard,
            groups: isObj(p.groups) ? (p.groups as Record<string, Group>) : seed.groups,
            sessionsByGroup: isObj(p.sessionsByGroup)
              ? (p.sessionsByGroup as Record<string, GroupSession[]>)
              : seed.sessionsByGroup,
            candidates: isObj(p.candidates) ? (p.candidates as Record<string, Candidate>) : seed.candidates,
            expedientes: isObj(p.expedientes) ? (p.expedientes as Record<string, Expediente>) : seed.expedientes,
            evaluations: isObj(p.evaluations) ? (p.evaluations as Record<string, Evaluation>) : {},
            dictamenes: isObj(p.dictamenes) ? (p.dictamenes as Record<string, Dictamen>) : {},
            users: isObj(p.users) ? (p.users as Record<string, AppUser>) : seed.users,
            auditLog: Array.isArray(p.auditLog) ? (p.auditLog as AuditLogEntry[]) : [],
            enrollments: isObj(p.enrollments) ? (p.enrollments as Record<string, string[]>) : seed.enrollments,
            expedienteDocs: isObj(p.expedienteDocs) ? (p.expedienteDocs as Record<string, ExpedienteDoc[]>) : seed.expedienteDocs,
            examAttempts: isObj(p.examAttempts) ? (p.examAttempts as Record<string, ExamAttempt[]>) : seed.examAttempts,
            diagnosticBankByStandard: isObj(p.diagnosticBankByStandard) ? (p.diagnosticBankByStandard as Record<string, DiagnosticBankItem[]>) : seed.diagnosticBankByStandard,
            theoreticalBankByStandard: isObj(p.theoreticalBankByStandard) ? (p.theoreticalBankByStandard as Record<string, TheoreticalBankQuestion[]>) : seed.theoreticalBankByStandard,
            prechecklistTemplateByStandard: isObj(p.prechecklistTemplateByStandard) ? (p.prechecklistTemplateByStandard as Record<string, PrechecklistItem[]>) : seed.prechecklistTemplateByStandard,
            prechecklistByExpediente: isObj(p.prechecklistByExpediente) ? (p.prechecklistByExpediente as Record<string, PrechecklistState>) : seed.prechecklistByExpediente,
            practicalScoresByExpediente: isObj(p.practicalScoresByExpediente) ? (p.practicalScoresByExpediente as Record<string, Record<string, PracticalScore>>) : seed.practicalScoresByExpediente,
            practicalNotesByExpediente: isObj(p.practicalNotesByExpediente) ? (p.practicalNotesByExpediente as Record<string, string>) : seed.practicalNotesByExpediente,
            attendanceByGroup: isObj(p.attendanceByGroup) ? (p.attendanceByGroup as DemoState["attendanceByGroup"]) : seed.attendanceByGroup,
            progressByExpediente: isObj(p.progressByExpediente) ? (p.progressByExpediente as DemoState["progressByExpediente"]) : seed.progressByExpediente,
            materialsByGroup: isObj(p.materialsByGroup) ? (p.materialsByGroup as DemoState["materialsByGroup"]) : seed.materialsByGroup,
            observationsByCandidate: isObj(p.observationsByCandidate) ? (p.observationsByCandidate as DemoState["observationsByCandidate"]) : seed.observationsByCandidate,
            examAvailabilityByExpediente: isObj(p.examAvailabilityByExpediente) ? (p.examAvailabilityByExpediente as DemoState["examAvailabilityByExpediente"]) : seed.examAvailabilityByExpediente,
            lmsRouteByStandard: (() => {
              if (!isObj(p.lmsRouteByStandard)) return seed.lmsRouteByStandard;
              const persisted = p.lmsRouteByStandard as DemoState["lmsRouteByStandard"];
              // Detectar IDs de formato antiguo (ej: "c-6") — no tienen el patrón "c-eNNNN-"
              const OLD_CRIT = /^c-\d+$/;
              const hasOldIds = Object.values(persisted).some((route) =>
                (route as { modules?: { criteriosCubiertos?: string[] }[] }).modules?.some((m) =>
                  m.criteriosCubiertos?.some((id) => OLD_CRIT.test(id))
                )
              );
              return hasOldIds ? seed.lmsRouteByStandard : persisted;
            })(),
            lmsProgressByExpediente: isObj(p.lmsProgressByExpediente) ? (p.lmsProgressByExpediente as DemoState["lmsProgressByExpediente"]) : seed.lmsProgressByExpediente,
            preopRunsByExpediente: isObj(p.preopRunsByExpediente) ? (p.preopRunsByExpediente as DemoState["preopRunsByExpediente"]) : seed.preopRunsByExpediente,
            iaConversationsByRole: isObj(p.iaConversationsByRole) ? (p.iaConversationsByRole as DemoState["iaConversationsByRole"]) : {},
          };
          set(safe);
          return { ok: true };
        } catch (e) {
          return { ok: false, error: (e as Error).message };
        }
      },
    }),
    {
      name: "certicore-demo-state",
      version: SCHEMA_VERSION,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => {
        const { iaConversationsByRole: _ia, ...rest } = state;
        return rest;
      },
      // Salvaguarda: si en el futuro alguien sube SCHEMA_VERSION sin pensarlo,
      // NO queremos borrar el trabajo del usuario (estándares dados de alta,
      // ediciones, dictámenes, etc.). Conservamos el estado persistido tal cual
      // y dejamos que el merge default rellene cualquier campo nuevo desde la
      // semilla en memoria. Para resetear la demo, el usuario debe usar
      // "Restablecer demo" en el menú de Utilidades o borrar el estándar.
      migrate: (persisted) => persisted as DemoState,
      // merge se ejecuta siempre al rehidratar (no depende de versión).
      // Lo usamos para reparar IDs de criteriosCubiertos en formato antiguo
      // (ej: "c-6") por los IDs canónicos del seed actual ("c-e5637-d1").
      merge: (persistedState, currentState) => {
        const p = persistedState as Partial<DemoState>;
        const fullSeedRoute = () => buildSeed({ includeEc1781: true }).lmsRouteByStandard;
        const OLD_CRIT = /^c-\d+$/;
        let lmsRouteByStandard = p.lmsRouteByStandard ?? currentState.lmsRouteByStandard;
        if (p.lmsRouteByStandard) {
          // Caso 1: IDs en formato antiguo ("c-6") → reemplazar con seed canónico.
          const hasOldIds = Object.values(p.lmsRouteByStandard).some((route) =>
            route.modules?.some((m) => m.criteriosCubiertos?.some((id) => OLD_CRIT.test(id)))
          );
          // Caso 2: la ruta EC1781 quedó vacía (0 módulos) por un merge anterior roto.
          const ec1781 = p.lmsRouteByStandard["EC1781"];
          const hasEmptyEc1781 = !ec1781 || ec1781.modules.length === 0;
          if (hasOldIds || hasEmptyEc1781) {
            // freshSeed() usa includeEc1781:false → usar buildSeed completo.
            lmsRouteByStandard = fullSeedRoute();
          }
        }
        return { ...currentState, ...p, lmsRouteByStandard };
      },
    }
  )
);

export type { Rule, AppUser, AuditLogEntry } from "@/data/seed";
